import { AnalyticsService } from '@alison/analytics/client';
import { FormFieldLabel, radius } from '@alison/ui';
import AddOutlined from '@mui/icons-material/AddOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import LightbulbOutlined from '@mui/icons-material/LightbulbOutlined';
import RemoveOutlined from '@mui/icons-material/RemoveOutlined';
import SwapHorizOutlined from '@mui/icons-material/SwapHorizOutlined';
import TrendingUpOutlined from '@mui/icons-material/TrendingUpOutlined';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { RecommendationsIcon } from './RecommendationsIcon.js';
import {
  type BestPracticeTip,
  bestPracticeTips,
  type ChangeAction,
  type CreativeRecommendation,
  creativeRecommendations,
} from './recommendations.js';
import { toneOf } from './ScoreVisuals.js';

const VISIBLE_CARDS = 2;
const FEATURE_SCORE_MAX = 4;
const SCORE_SCALE = 10;

const ACTION_ICON: Record<ChangeAction, typeof AddOutlined> = {
  Add: AddOutlined,
  Replace: SwapHorizOutlined,
  Remove: RemoveOutlined,
};

const WRAPPER_SX = {
  gap: 3,
  p: 3,
  borderRadius: 1.2,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.platform',
};

const CARD_SX = {
  gap: 2,
  px: 3,
  py: 2.5,
  borderRadius: 2,
  bgcolor: 'background.page',
};

const PILL_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  px: 1.5,
  py: 0.25,
  borderRadius: `${radius.pill}px`,
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};

const BADGE_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0.5,
  px: 2,
  py: 0.5,
  borderRadius: 1,
  minWidth: 60,
  fontSize: 14,
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

const COLUMN_LABEL_SX = { fontSize: 9, color: 'text.muted' };

const CHANGE_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'minmax(180px, 1fr) minmax(110px, 1fr) auto minmax(110px, 1fr)',
  },
  alignItems: 'end',
  justifyItems: 'start',
  columnGap: 5,
  rowGap: 1.5,
};

const CELL_SX = { gap: 0.5, alignItems: 'flex-start', textAlign: 'left', minWidth: 0 };

const WHY_SX = {
  gap: 1,
  pt: 2,
  alignItems: 'flex-start',
  borderTop: '1px solid',
  borderColor: 'divider',
};

const WHY_EXPLANATION = 'What this change is expected to improve, and why it was suggested.';

export interface RecommendationsSectionProps {
  result: unknown;
  creativeId: string;
  networkId: number;
}

function Lift({ delta }: { delta: number }): ReactNode {
  const theme = useTheme();
  const tone = delta >= 0 ? theme.palette.score.high : theme.palette.score.low;
  return (
    <Box component="span" sx={{ ...BADGE_SX, color: tone.main, bgcolor: tone.bg }}>
      {delta >= 0 ? '+' : ''}
      {delta.toFixed(1)}
      <TrendingUpOutlined sx={{ fontSize: 14, transform: delta >= 0 ? 'none' : 'scaleY(-1)' }} />
    </Box>
  );
}

function PerformanceCard({
  recommendation,
}: {
  recommendation: CreativeRecommendation;
}): ReactNode {
  const theme = useTheme();
  const actions = [...new Set(recommendation.changes.map((change) => change.action))];

  return (
    <Stack sx={CARD_SX}>
      <Stack direction="row" sx={{ gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography sx={{ flex: 1, minWidth: 120, fontSize: 13, fontWeight: 700 }}>
          {recommendation.title}
        </Typography>
        {actions.map((action) => {
          const Icon = ACTION_ICON[action];
          return (
            <Box
              component="span"
              key={action}
              sx={{ ...PILL_SX, color: 'primary.main', bgcolor: 'primary.light' }}
            >
              <Icon sx={{ fontSize: 14 }} />
              {action}
            </Box>
          );
        })}
        <Lift delta={recommendation.delta} />
      </Stack>

      <Stack sx={{ gap: 1.5 }}>
        {recommendation.changes.map((change) => (
          <Box key={`${change.element}-${change.proposed}`} sx={CHANGE_GRID_SX}>
            <Stack sx={CELL_SX}>
              <FormFieldLabel sx={COLUMN_LABEL_SX}>ELEMENT</FormFieldLabel>
              <Typography sx={{ fontWeight: 600 }}>{change.element}</Typography>
            </Stack>
            <Stack sx={CELL_SX}>
              <FormFieldLabel sx={COLUMN_LABEL_SX}>CURRENT</FormFieldLabel>
              <Typography sx={{ color: 'text.muted' }}>{change.current}</Typography>
            </Stack>
            <Typography
              aria-hidden
              sx={{ display: { xs: 'none', sm: 'block' }, color: 'text.disabled' }}
            >
              →
            </Typography>
            <Stack sx={CELL_SX}>
              <FormFieldLabel sx={COLUMN_LABEL_SX}>RECOMMENDED</FormFieldLabel>
              <Typography sx={{ fontWeight: 600, color: theme.palette.score.high.main }}>
                {change.proposed}
              </Typography>
            </Stack>
          </Box>
        ))}
      </Stack>

      <Stack direction="row" sx={WHY_SX}>
        <Tooltip placement="top-start" title={WHY_EXPLANATION}>
          <InfoOutlined sx={{ fontSize: 16, mt: 0.25, color: 'text.muted', flexShrink: 0 }} />
        </Tooltip>
        <Typography sx={{ color: 'text.muted' }}>
          <Box component="strong" sx={{ color: 'text.primary' }}>
            Why:
          </Box>{' '}
          {recommendation.rationale}
        </Typography>
      </Stack>
    </Stack>
  );
}

function BestPracticeCard({ tip }: { tip: BestPracticeTip }): ReactNode {
  const theme = useTheme();
  const tone = toneOf(
    theme,
    tip.score === null ? null : (tip.score / FEATURE_SCORE_MAX) * SCORE_SCALE,
  );

  return (
    <Stack sx={CARD_SX}>
      <Stack direction="row" sx={{ gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        {tip.abcd === null ? null : (
          <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{tip.abcd}</Typography>
        )}
        <Box component="span" sx={{ ...PILL_SX, color: 'text.muted', bgcolor: 'background.page' }}>
          {tip.attribute}
        </Box>
        <Typography sx={{ fontSize: 13 }}>
          <Box component="span" sx={{ color: 'text.muted' }}>
            Feature:
          </Box>{' '}
          {tip.feature}
        </Typography>
        <Typography sx={{ fontSize: 13 }}>
          <Box component="span" sx={{ color: 'text.muted' }}>
            Tag:
          </Box>{' '}
          {tip.tag}
        </Typography>
        <LightbulbOutlined sx={{ fontSize: 18, color: 'warning.main' }} />
        <Box component="span" sx={{ ...BADGE_SX, ml: 'auto', color: tone }}>
          {tip.score === null ? '—' : tip.score.toFixed(1)}
        </Box>
      </Stack>
      <Typography sx={{ color: 'text.muted' }}>{tip.text}</Typography>
    </Stack>
  );
}

function GroupHeading({ title, subtitle }: { title: string; subtitle: string }): ReactNode {
  return (
    <Box>
      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{title}</Typography>
      <Typography sx={{ color: 'text.muted' }}>{subtitle}</Typography>
    </Box>
  );
}

export function RecommendationsSection({
  result,
  creativeId,
  networkId,
}: RecommendationsSectionProps): ReactNode {
  const [showMore, setShowMore] = useState(false);
  const written = creativeRecommendations(result, creativeId, networkId);
  const tips = bestPracticeTips(result, creativeId, networkId);

  const groups = [
    {
      key: 'performance',
      title: 'Performance recommendations',
      subtitle: 'Tag changes projected to lift the overall score',
      cards: written.map((recommendation) => (
        <PerformanceCard key={`written-${recommendation.id}`} recommendation={recommendation} />
      )),
    },
    {
      key: 'best-practice',
      title: 'Best practice tips',
      subtitle: 'Per-feature guidance from the platform’s creative framework',
      cards: tips.map((tip) => (
        <BestPracticeCard key={`tip-${tip.feature}-${tip.tag}`} tip={tip} />
      )),
    },
  ].filter((group) => group.cards.length > 0);

  const total = groups.reduce((count, group) => count + group.cards.length, 0);
  if (total === 0) return null;

  let seen = 0;
  const blocks = groups.map((group) => {
    const start = seen;
    seen += group.cards.length;
    const visibleCount = Math.max(0, Math.min(group.cards.length, VISIBLE_CARDS - start));
    const heading = <GroupHeading key="heading" title={group.title} subtitle={group.subtitle} />;
    return {
      key: group.key,
      visible: visibleCount === 0 ? [] : [heading, ...group.cards.slice(0, visibleCount)],
      hidden: [...(visibleCount === 0 ? [heading] : []), ...group.cards.slice(visibleCount)],
    };
  });

  return (
    <Stack sx={WRAPPER_SX}>
      <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
        <RecommendationsIcon sx={{ fontSize: 34, color: 'primary.main' }} />
        <Box>
          <Typography variant="h3" sx={{ fontSize: 15 }}>
            Recommendations
          </Typography>
          <Typography sx={{ color: 'text.muted' }}>
            {`${total} action${total === 1 ? '' : 's'} to improve your creative score`}
          </Typography>
        </Box>
      </Stack>

      <Stack
        data-testid="creative-recommendations"
        sx={{
          gap: 3,
          p: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        {blocks.map((block) => (
          <Stack key={block.key} sx={{ gap: 2 }}>
            {block.visible}
          </Stack>
        ))}

        {total <= VISIBLE_CARDS ? null : (
          <>
            <Collapse in={showMore}>
              <Stack sx={{ gap: 3 }}>
                {blocks
                  .filter((block) => block.hidden.length > 0)
                  .map((block) => (
                    <Stack key={block.key} sx={{ gap: 2 }}>
                      {block.hidden}
                    </Stack>
                  ))}
              </Stack>
            </Collapse>
            <Link
              component="button"
              onClick={() => {
                if (!showMore) {
                  AnalyticsService.track('Workflow - Recommendations Expanded', { creativeId });
                }
                setShowMore(!showMore);
              }}
              sx={{ alignSelf: 'center', fontSize: 13, cursor: 'pointer' }}
            >
              {showMore ? 'Show less' : `Show ${total - VISIBLE_CARDS} more`}
            </Link>
          </>
        )}
      </Stack>
    </Stack>
  );
}
